import { useEffect, useState } from 'react'
import { Button, OnsaLogo } from '../../onsaui'
import useDeeplink from '../../modules/auth/hooks/useDeeplink'
import useAuth from '../../modules/auth/hooks/useAuth'
import { FiUsers, FiFileText, FiMail } from 'react-icons/fi'
import zoomSdk from '@zoom/appssdk'

const InstallerPage: React.FC = () => {
  const { isAuth } = useAuth()
  const { loadDeeplink } = useDeeplink()
  const [deeplink, setDeeplink] = useState<string | null>(null)

  const openDeeplink = async () => {
    if (!deeplink) {
      console.error('No deeplink found')
      return
    }

    if (isAuth) {
      // means, we are inside the zoom client
      await zoomSdk.openUrl({ url: deeplink })
    } else {
      window.open(deeplink)
    }
  }

  useEffect(() => {
    const fetchDeeplink = async () => {
      const deeplink = await loadDeeplink()
      setDeeplink(deeplink)
    }

    fetchDeeplink()
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-blue-50 to-white">
      <header className="w-full border-b border-gray-200 bg-white p-4 shadow-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center space-x-2">
            <OnsaLogo />
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-10 md:py-16">
        <div className="grid gap-12 md:grid-cols-2">
          <div className="flex flex-col justify-center space-y-6">
            <h1 className="text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
              Your AI Sales Associate <span className="text-blue-600">for Zoom</span>
            </h1>

            <p className="text-lg text-gray-700">
              Onsa.ai joins your calls as a bot, transcribes meetings in real-time, and provides
              valuable insights about participants to boost your sales conversations.
            </p>

            <div className="flex flex-col space-y-4">
              {deeplink ? (
                <Button
                  onClick={openDeeplink}
                  className="w-full rounded-lg bg-blue-600 px-6 py-3 text-center text-lg font-medium text-white shadow-lg transition-all hover:bg-blue-700 md:w-auto"
                >
                  Install Onsa.ai on Zoom
                </Button>
              ) : (
                <div className="flex items-center space-x-2 text-gray-500">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                  <span>Preparing your installation...</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col justify-center space-y-8 rounded-xl border border-gray-200 bg-white p-6 shadow-md">
            <h2 className="text-2xl font-semibold text-gray-800">Key Features</h2>

            <div className="flex flex-col space-y-6">
              <div className="flex space-x-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <FiFileText size={20} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Live Transcription</h3>
                  <p className="text-gray-600">
                    Capture every important detail from your meetings automatically
                  </p>
                </div>
              </div>

              <div className="flex space-x-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <FiUsers size={20} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Participant Insights</h3>
                  <p className="text-gray-600">
                    Get valuable information about meeting participants in real-time
                  </p>
                </div>
              </div>

              <div className="flex space-x-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <FiMail size={20} />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Automated Follow-ups</h3>
                  <p className="text-gray-600">
                    Send meeting transcripts and concise summaries to participants
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-20">
          <h2 className="mb-10 text-center text-2xl font-bold text-gray-800">How Onsa.ai Works</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="rounded-lg border border-gray-200 bg-white p-5 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                1
              </div>
              <h3 className="mb-2 text-lg font-medium">Install the App</h3>
              <p className="text-gray-600">Add Onsa.ai to your Zoom account with one click</p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                2
              </div>
              <h3 className="mb-2 text-lg font-medium">Invite to Meetings</h3>
              <p className="text-gray-600">Add Onsa.ai bot to your sales calls</p>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-5 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                3
              </div>
              <h3 className="mb-2 text-lg font-medium">Get Insights & Follow-ups</h3>
              <p className="text-gray-600">Receive transcripts and AI-powered meeting summaries</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="mt-auto border-t border-gray-200 bg-white py-8">
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex flex-col items-center justify-between space-y-4 text-sm text-gray-500 md:flex-row md:space-y-0">
            <div>© {new Date().getFullYear()} Onsa.ai. All rights reserved.</div>
            <div className="flex space-x-6">
              <a href="/privacy-policy" className="hover:text-blue-600">
                Privacy Policy
              </a>
              <a href="/terms-and-conditions" className="hover:text-blue-600">
                Terms of Service
              </a>
              <a href="/support" className="hover:text-blue-600">
                Support
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default InstallerPage
